import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Pencil, Archive, Plus, Copy } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";

const BLANK = {
  name: "", slug: "", short_description: "", full_description: "",
  image_url: "", category: "", subject_ids: [], price: 0, discount_price: null,
  currency: "INR", duration: "", course_type: "online",
  start_date: "", instructor_name: "", instructor_bio: "",
  features: [], prerequisites: "", plan_type: "paid",
  status: "published", is_visible: true, order: 0, access_days: null, buy_enabled: true,
};

export default function CoursesTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [featuresText, setFeaturesText] = useState("");

  const [subjects, setSubjects] = useState([]);
  const load = () => api.get("/admin/courses").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/subjects").then((r) => setSubjects(r.data)); }, []);
  const toggleSubject = (id) => setForm((f) => ({ ...f, subject_ids: (f.subject_ids || []).includes(id) ? f.subject_ids.filter((x) => x !== id) : [...(f.subject_ids || []), id] }));

  const openCreate = () => { setEditing(null); setForm(BLANK); setFeaturesText(""); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...BLANK, ...c, discount_price: c.discount_price ?? null });
    setFeaturesText((c.features || []).join("\n"));
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        price: Number(form.price) || 0,
        discount_price: form.discount_price === "" || form.discount_price == null ? null : Number(form.discount_price),
        order: Number(form.order) || 0,
        access_days: form.access_days ? Number(form.access_days) : null,
        features: featuresText.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      if (editing) await api.put(`/courses/${editing.id}`, payload);
      else await api.post("/courses", payload);
      toast.success(editing ? "Course updated" : "Course created");
      setOpen(false); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };
  const del = async (c) => {
    if (!confirm(`Archive "${c.name}"? It will be hidden from the website. Enrollments and payment records are kept.`)) return;
    await api.delete(`/courses/${c.id}`);
    toast.success("Course archived"); load();
  };
  const duplicate = async (c) => {
    await api.post(`/courses/${c.id}/duplicate`);
    toast.success("Duplicated as draft"); load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={openCreate} className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="new-course-btn">
          <Plus className="mr-2 h-4 w-4" /> Add course
        </Button>
      </div>
      <Card className="border-border/70">
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Visible</TableHead>
              <TableHead>Buy Now</TableHead>
              <TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-muted-foreground">/{c.slug}</div>
                  </TableCell>
                  <TableCell className="text-xs">{c.category || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {c.plan_type === "free" ? "Free" :
                      (c.discount_price != null && c.discount_price < c.price)
                        ? `₹${c.discount_price} (was ₹${c.price})`
                        : `₹${c.price}`}
                  </TableCell>
                  <TableCell className="capitalize">{c.status}</TableCell>
                  <TableCell>{c.is_visible ? "Yes" : "No"}</TableCell>
                  <TableCell>
                    <Switch checked={c.buy_enabled !== false} onCheckedChange={async (v) => { await api.put(`/courses/${c.id}`, { ...BLANK, ...c, buy_enabled: v }); toast.success(v ? "Buy Now enabled" : "Buy Now disabled"); load(); }} data-testid={`buy-toggle-${c.id}`} />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => duplicate(c)} data-testid={`dup-course-${c.id}`}><Copy className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-course-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" title="Archive" onClick={() => del(c)} data-testid={`del-course-${c.id}`}><Archive className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit course" : "New course"}</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="course-name" /></div>
              <div><Label>Slug (URL)</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto from name" data-testid="course-slug" /></div>
            </div>
            <div><Label>Short description</Label><Textarea rows={2} value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} data-testid="course-short" /></div>
            <div><Label>Full description</Label><Textarea rows={4} value={form.full_description} onChange={(e) => setForm({ ...form, full_description: e.target.value })} data-testid="course-full" /></div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="course-category" /></div>
              <div><Label>Image URL</Label><Input value={form.image_url || ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="course-image" /></div>
              <div><Label>Duration</Label><Input value={form.duration || ""} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="e.g. 6 months" data-testid="course-duration" /></div>
              <div><Label>Access days (blank = lifetime)</Label><Input type="number" min={1} value={form.access_days ?? ""} placeholder="Lifetime" onChange={(e) => setForm({ ...form, access_days: e.target.value })} data-testid="course-access-days" /></div>
            </div>
            <div className="grid gap-3 sm:grid-cols-4">
              <div><Label>Price (₹)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="course-price" /></div>
              <div><Label>Discount price (₹)</Label><Input type="number" value={form.discount_price ?? ""} onChange={(e) => setForm({ ...form, discount_price: e.target.value })} data-testid="course-discount" /></div>
              <div>
                <Label>Plan type</Label>
                <Select value={form.plan_type} onValueChange={(v) => setForm({ ...form, plan_type: v })}>
                  <SelectTrigger data-testid="course-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Course type</Label>
                <Select value={form.course_type} onValueChange={(v) => setForm({ ...form, course_type: v })}>
                  <SelectTrigger data-testid="course-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="online">Online</SelectItem>
                    <SelectItem value="offline">Offline</SelectItem>
                    <SelectItem value="live">Live</SelectItem>
                    <SelectItem value="recorded">Recorded</SelectItem>
                    <SelectItem value="self-paced">Self-paced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label>Instructor name</Label><Input value={form.instructor_name || ""} onChange={(e) => setForm({ ...form, instructor_name: e.target.value })} data-testid="course-instructor" /></div>
              <div><Label>Start date</Label><Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="course-start" /></div>
            </div>
            <div><Label>Instructor bio</Label><Textarea rows={2} value={form.instructor_bio || ""} onChange={(e) => setForm({ ...form, instructor_bio: e.target.value })} data-testid="course-instructor-bio" /></div>
            <div>
              <Label>Subjects included (students who buy get these subjects, all chapters & questions)</Label>
              <div className="mt-2 grid gap-2 rounded-lg border border-border p-3 sm:grid-cols-2" data-testid="course-subjects-picker">
                {subjects.map((sub) => (
                  <label key={sub.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={(form.subject_ids || []).includes(sub.id)} onCheckedChange={() => toggleSubject(sub.id)} data-testid={`course-subject-${sub.code}`} />
                    {sub.name} <span className="text-xs text-muted-foreground">({sub.topic_count} chapters · {sub.question_count} Qs)</span>
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-3 rounded-lg border border-border p-3 text-sm">
              <Switch checked={form.buy_enabled !== false} onCheckedChange={(v) => setForm({ ...form, buy_enabled: v })} data-testid="course-buy-enabled" />
              Show "Buy Now" and allow online enrollment
            </label>
            <div><Label>Features (one per line)</Label><Textarea rows={4} value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} data-testid="course-features" /></div>
            <div><Label>Prerequisites</Label><Textarea rows={2} value={form.prerequisites || ""} onChange={(e) => setForm({ ...form, prerequisites: e.target.value })} data-testid="course-prereq" /></div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="course-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Visible on site</Label>
                <Select value={form.is_visible ? "yes" : "no"} onValueChange={(v) => setForm({ ...form, is_visible: v === "yes" })}>
                  <SelectTrigger data-testid="course-visible"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Visible</SelectItem>
                    <SelectItem value="no">Hidden</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Order</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="course-order" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="course-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
